/**
 * ConversationInfoTab
 * Aba de informações gerais da conversa (contato, canal, atendente, status, datas, tags)
 */

import React from 'react';
import { TagChip } from '@/shared/components/ui';
import type { ConversationResponse } from '@troia-v3/types';

interface ConversationInfoTabProps {
  conversation: ConversationResponse;
}

const statusLabel: Record<string, string> = {
  waiting: 'Aguardando',
  active: 'Em atendimento',
  closed: 'Encerrada',
};

const statusColor: Record<string, { bg: string; text: string }> = {
  waiting: { bg: '#fef3c7', text: '#92400e' },
  active: { bg: '#dcfce7', text: '#166534' },
  closed: { bg: '#f1f5f9', text: '#64748b' },
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

interface InfoCardProps {
  label: string;
  children: React.ReactNode;
}

const InfoCard: React.FC<InfoCardProps> = ({ label, children }) => (
  <div
    className="rounded-lg p-3"
    style={{
      backgroundColor: '#f8fafc',
      border: '1px solid #e5e7eb',
    }}
  >
    <p className="text-xs font-medium mb-1" style={{ color: '#94a3b8' }}>
      {label}
    </p>
    <div className="text-sm font-medium" style={{ color: '#1e293b' }}>
      {children}
    </div>
  </div>
);

export const ConversationInfoTab: React.FC<ConversationInfoTabProps> = ({ conversation }) => {
  const isGroup = conversation.conversationType === 'group';
  const status = conversation.status;
  const statusStyle = statusColor[status] ?? statusColor.closed;

  return (
    <div className="p-4 space-y-4">
      {/* Contact / Group info */}
      {isGroup ? (
        <div className="grid grid-cols-2 gap-3">
          <InfoCard label="Nome do grupo">
            {conversation.group?.name ?? '—'}
          </InfoCard>
          <InfoCard label="Membros">
            {conversation.members?.length ?? 0} membros
          </InfoCard>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <InfoCard label="Contato">
            {conversation.contact?.name ?? '—'}
          </InfoCard>
          <InfoCard label="Telefone">
            {conversation.contact?.phone ?? '—'}
          </InfoCard>
        </div>
      )}

      {/* Canal, Atendente, Status */}
      <div className="grid grid-cols-3 gap-3">
        <InfoCard label="Canal">
          {conversation.provider?.name ?? '—'}
        </InfoCard>
        <InfoCard label="Atendente">
          {conversation.assignee?.name ?? 'Sem atendente'}
        </InfoCard>
        <InfoCard label="Status">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
          >
            {statusLabel[status] ?? status}
          </span>
        </InfoCard>
      </div>

      {/* Datas */}
      <div className="grid grid-cols-2 gap-3">
        <InfoCard label="Iniciada em">
          {formatDate(conversation.startedAt || conversation.createdAt)}
        </InfoCard>
        <InfoCard label="Última mensagem">
          {formatDate(conversation.lastMessageAt)}
        </InfoCard>
      </div>

      {/* Tags */}
      <InfoCard label="Tags">
        {conversation.tags && conversation.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {conversation.tags.map((tag, i) => (
              <TagChip
                key={i}
                name={tag}
                className="text-xs"
                style={{ padding: '2px 6px' }}
              />
            ))}
          </div>
        ) : (
          <span style={{ color: '#94a3b8' }}>Sem tags</span>
        )}
      </InfoCard>

      {/* Priority */}
      <div className="grid grid-cols-2 gap-3">
        <InfoCard label="Prioridade">
          {conversation.priority === 'low' && 'Baixa'}
          {conversation.priority === 'normal' && 'Normal'}
          {conversation.priority === 'high' && 'Alta'}
          {conversation.priority === 'urgent' && 'Urgente'}
          {!conversation.priority && '—'}
        </InfoCard>
        <InfoCard label="Mensagens">
          {conversation.messageCount ?? 0}
        </InfoCard>
      </div>
    </div>
  );
};

export default ConversationInfoTab;
